import { useState, useEffect, useCallback } from 'react';
import { useUser } from './use-user';
import { queryClient } from '../queryClient';
import { fetchManagedHubs, fetchCurrentHub, switchCurrentHub, clearApiCache } from '../api-helpers';
import { SessionService } from '../sessionService';

export interface Hub {
  id: string;
  name: string;
  description?: string | null;
  ownerId: string;
  owner_user_id?: string | null;
  isPublic: boolean;
  createdAt: string;
  avatarInitials?: string;
  avatarUrl?: string | null;
  themePreference?: string | null;
  likes?: string | null;
  dislikes?: string | null;
  strengths?: string | null;
  whatWorksForMe?: string | null;
  notes?: string | null;
  enableCareRecipientView?: boolean;
  careRecipientPin?: string | null;
  preferences?: string | null;
  role?: string;
}

export interface CurrentHubResponse {
  hubId: string;
  hub: Hub;
}

// -----------------------------
// GLOBAL HUB SETTER
// -----------------------------
async function setCurrentHub(hubId: string | null, hubName?: string) {
  if (hubId) {
    window.__currentHubId = hubId;
    document.body.dataset.hubId = hubId;
    await SessionService.setCurrentHub(hubId);
    console.log(`[Hub Isolation] Current hub set → ${hubId}${hubName ? ` (${hubName})` : ''}`);
  } else {
    window.__currentHubId = undefined;
    document.body.dataset.hubId = '';
    await SessionService.clearSession?.();
    console.log('[Hub Isolation] Hub cleared');
  }

  if (window.directTimerSyncAll) {
    window.directTimerSyncAll();
  }
}

// -----------------------------
// HOOK: useCurrentHub
// -----------------------------

// Module-level cache for hub data (persists across component mounts)
let cachedHubData: CurrentHubResponse | null = null;
let cachedManagedHubs: Hub[] = [];
let cachedForUserId: string | null = null; // Track which user the cache belongs to

/** Exported for use when user identity changes — forces a fresh hub fetch */
export function clearHubCache() {
  cachedHubData = null;
  cachedManagedHubs = [];
  cachedForUserId = null;
  if (typeof window !== 'undefined') {
    window.__currentHubId = undefined;
    (window as any).__hubInitCalled = false;
    (window as any).__hubInitUserId = null;
  }
  clearApiCache(); // Also clear the api-helpers 5s cache
  console.log('[HUB CACHE] Cleared due to user identity change');
}

// ────────────────────────────────────────────────────────────────
// HELPER: Extract hub ID from URL (pure function, no side effects)
// ────────────────────────────────────────────────────────────────
function getHubIdFromPathname(): string | undefined {
  if (typeof window === 'undefined') return undefined;
  
  // Match /hub/{uuid}/... or /care/{uuid}/...
  const match = window.location.pathname.match(/\/(?:hub|care)\/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})/i);
  return match?.[1];
}

export function useCurrentHub() {
  const { user, isLoading: userLoading } = useUser() as { user: any | null; isLoading: boolean };
  
  // Extract hubId from URL on each render - React-owned, no global mutation
  const urlHubId = getHubIdFromPathname();
  
  // Initialize from cache or window.__currentHubId if available
  const [currentHubData, setCurrentHubData] = useState<CurrentHubResponse | null>(() => {
    // If we have cached data, use it immediately
    if (cachedHubData) return cachedHubData;
    return null;
  });
  const [managedHubs, setManagedHubs] = useState<Hub[]>(() => cachedManagedHubs);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  // Only trust the URL hub if the user actually has access to it.
  // When managedHubs are loaded, validate; otherwise allow it through so initial
  // navigation still works (validation kicks in on the next render cycle).
  const urlHubIsAccessible =
    !urlHubId ||
    cachedManagedHubs.length === 0 ||
    cachedManagedHubs.some((h) => h.id === urlHubId);

  // Derive hubId: URL first (if accessible), then cached data, then window global
  const hubId = (urlHubIsAccessible ? urlHubId : undefined) || currentHubData?.hub?.id || (typeof window !== 'undefined' ? window.__currentHubId : undefined) || '';

  if (urlHubId && !urlHubIsAccessible) {
    console.warn('[useCurrentHub] URL hub', urlHubId, 'not in managed hubs — falling back to session hub', currentHubData?.hubId);
  }
  
  // Keep window.__currentHubId in sync (for other code that depends on it)
  useEffect(() => {
    if (hubId && hubId !== window.__currentHubId) {
      window.__currentHubId = hubId;
      console.log('[HUB_SYNC] Updated window.__currentHubId:', hubId);
    }
  }, [hubId]);

  // When the URL points to an accessible hub that differs from the server session,
  // push the new hub to the server session.  This is the gap that causes the
  // "URL hub vs session hub" mismatch: navigation updates the URL (and therefore
  // window.__currentHubId) but never calls setCurrentHub(), so the server session
  // stays pinned to whichever hub was active at login time.
  useEffect(() => {
    if (!urlHubIsAccessible || !urlHubId) return;
    // cachedHubData reflects what the server session last confirmed — if the URL
    // hub already matches, there is nothing to do.
    if (urlHubId === cachedHubData?.hubId) return;

    console.log('[HUB_SYNC] URL hub differs from server session hub — syncing:', {
      urlHubId,
      serverSessionHub: cachedHubData?.hubId ?? '(none)',
    });

    // ── Optimistic cache stamp ──────────────────────────────────────────────
    // Update cachedHubData SYNCHRONOUSLY before the async server call so that
    // any other mounted instances of useCurrentHub see the cache as already
    // matching and skip their own redundant setCurrentHub() call.
    // Without this, every instance that evaluates (urlHubId !== cachedHubData?.hubId)
    // at mount time fires an independent sync — visible as repeated [HUB_SYNC] logs.
    const matchingHub = cachedManagedHubs.find(h => h.id === urlHubId);
    if (matchingHub) {
      cachedHubData = { hubId: urlHubId, hub: matchingHub };
    } else if (cachedHubData) {
      // Hub list not loaded yet — at least stamp the hubId so the guard holds.
      cachedHubData = { ...cachedHubData, hubId: urlHubId };
    }
    // ────────────────────────────────────────────────────────────────────────

    setCurrentHub(urlHubId).catch((err) =>
      console.warn('[HUB_SYNC] Failed to sync server session hub:', err)
    );
  }, [urlHubId, urlHubIsAccessible]);
  
  // Debug logging
  useEffect(() => {
    console.log('[useCurrentHub] State:', { 
      hubId, 
      hasCurrentHubData: !!currentHubData, 
      cachedHubId: cachedHubData?.hubId,
      windowHubId: typeof window !== 'undefined' ? window.__currentHubId : undefined,
      loading 
    });
  }, [hubId, currentHubData, loading]);

  const HUB_DEPENDENT_QUERIES = [
    '/api/schedules',
    '/api/social-narratives',
    '/api/reminders',
    '/api/timers',
    '/api/locations',
    '/api/choice-boards',
    '/api/reward-charts',
    '/api/community-feed',
    '/api/health',
    '/api/auth',
    '/api/videos',
    '/api/hubs',
    '/api/subscription',
    '/api/users',
    '/api/progress',
    '/api/health-data',
    '/api/messenger',
  ];

  // Helper to update both state and cache
  const updateHubData = (data: CurrentHubResponse | null) => {
    cachedHubData = data;
    setCurrentHubData(data);
  };
  
  const updateManagedHubs = (hubs: Hub[]) => {
    cachedManagedHubs = hubs;
    setManagedHubs(hubs);
  };

  // -----------------------------
  // INITIAL HUB LOAD (wait for authentication)
  // -----------------------------
  useEffect(() => {
    if (userLoading || !user?.isAuthenticated) return;

    // If we already have cached hub data for THIS user, use it and skip fetch
    const currentUserId = user?.id ?? null;
    if (cachedHubData && window.__currentHubId && cachedForUserId === currentUserId) {
      console.log('[HUB_INIT] Using cached hub data for user:', currentUserId, cachedHubData.hubId);
      setCurrentHubData(cachedHubData);
      setManagedHubs(cachedManagedHubs);
      return;
    }
    // Cache belongs to a different user — clear it and re-fetch
    if (cachedForUserId !== null && cachedForUserId !== currentUserId) {
      console.log('[HUB CACHE] User changed:', cachedForUserId, '→', currentUserId, '— clearing hub cache');
      cachedHubData = null;
      cachedManagedHubs = [];
      cachedForUserId = null;
      if (typeof window !== 'undefined') window.__currentHubId = undefined;
    }

    // Prevent double-loading in React 18 Strict Mode, but allow re-load when user changes
    const lastInitUserId = (window as any).__hubInitUserId;
    if ((window as any).__hubInitCalled && lastInitUserId === currentUserId) {
      // Same user, already called — only skip if we have a hub set
      if (!window.__currentHubId) {
        console.log('[HUB_INIT] Reset and retry - init was called but no hub set');
        (window as any).__hubInitCalled = false;
      } else {
        return;
      }
    }
    (window as any).__hubInitCalled = true;
    (window as any).__hubInitUserId = currentUserId;

    let isMounted = true;

    async function loadInitialHub() {
      setLoading(true);
      setError(null);

      try {
        const hubs = await fetchManagedHubs();
        if (!isMounted) return;
        cachedForUserId = currentUserId; // Tag cache with current user
        updateManagedHubs(hubs);

        if (hubs.length === 0) {
          updateHubData(null);
          await setCurrentHub(null);
          return;
        }

        let hubToUse = hubs[0];

        try {
          const currentHubResponse = await fetchCurrentHub();
          if (currentHubResponse?.hub) hubToUse = currentHubResponse.hub;
        } catch (fetchErr: any) {
          console.warn('[HUB_INIT] Could not fetch current hub, using fallback', fetchErr?.message || '');
        }

        const hubData: CurrentHubResponse = { hubId: hubToUse.id, hub: hubToUse };
        updateHubData(hubData);
        await setCurrentHub(hubToUse.id, hubToUse.name);
      } catch (err: any) {
        console.error('[HUB_INIT] Error:', err);
        if (isMounted) {
          setError(err instanceof Error ? err : new Error('Failed to initialize hubs'));
        }
        updateHubData(null);
        updateManagedHubs([]);
        window.__currentHubId = undefined;
      } finally {
        if (isMounted) setLoading(false);
      }
    }

    loadInitialHub();
    return () => { isMounted = false; };
  }, [user?.isAuthenticated, user?.id, userLoading]);

  // -----------------------------
  // SWITCH HUB
  // -----------------------------
  const switchHub = useCallback(
    async (hubId: string) => {
      if (!user?.isAuthenticated) return;

      setLoading(true);
      setError(null);

      try {
        await switchCurrentHub(hubId);

        await Promise.all(
          HUB_DEPENDENT_QUERIES.map((key) =>
            queryClient.invalidateQueries({ queryKey: [key] })
          )
        );

        const response = await fetchCurrentHub();
        updateHubData(response);
        if (response?.hub?.id) {
          await setCurrentHub(response.hub.id, response.hub.name);
        }

        console.log(`[HUB_SWITCH] Switched to hub → ${hubId}`);
      } catch (err: any) {
        console.error('[HUB_SWITCH] Error switching hub:', err);
        updateHubData(null);
        window.__currentHubId = undefined;

        setError(err instanceof Error ? err : new Error('Failed to switch hub'));
      } finally {
        setLoading(false);
      }
    },
    [user?.isAuthenticated]
  );

  // -----------------------------
  // RELOAD HUB LIST
  // -----------------------------
  const initializeHubs = useCallback(async () => {
    if (!user?.isAuthenticated) return;

    setLoading(true);
    setError(null);

    try {
      const hubs = await fetchManagedHubs();
      updateManagedHubs(hubs);

      if (hubs.length === 0) {
        updateHubData(null);
        await setCurrentHub(null);
        return;
      }

      let currentResponse: CurrentHubResponse | null = null;
      try { currentResponse = await fetchCurrentHub(); } catch {}

      const finalHub = currentResponse?.hub || hubs[0];
      const finalHubData: CurrentHubResponse = { hubId: finalHub.id, hub: finalHub };

      updateHubData(finalHubData);
      await setCurrentHub(finalHub.id, finalHub.name);
    } catch (err) {
      console.error('[HUB_INIT] Error:', err);
      setError(err instanceof Error ? err : new Error('Failed to initialize hubs'));
    } finally {
      setLoading(false);
    }
  }, [user?.isAuthenticated]);

  const reload = useCallback(async () => {
    if (!user?.isAuthenticated) return;
    return initializeHubs();
  }, [initializeHubs, user?.isAuthenticated]);

  // hubId is already defined above using multiple sources for reliability
  return {
    currentHubData,
    hubId,  // Uses the derived hubId from line 79
    managedHubs,
    loading,
    error,
    switchHub,
    reload,
  };
}
